import type { RetailerProfileEntity } from '@packages/domain/retailers';
import type { TechlineCaseEntity } from '@packages/domain/techline/cases';
import type { VinDetailEntity } from '@packages/domain/vin';

import { colors } from '@packages/core/styles';
import { Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';

import { CaseTeamSection } from './case-team-section';

/**
 * Props for the DraftSidebar component.
 */
export type DraftSidebarProps = {
    /** The case details for the current draft case. */
    caseDetails: TechlineCaseEntity;

    /** Whether the add-team-member button should be disabled. */
    isAddMemberDisabled: boolean;

    /** Callback to add a team member by username. */
    onAddTeamMember: (username: string) => Promise<boolean>;

    /** Callback to display an error message. */
    onError: (message: string) => void;

    /** The primary retailer profile for this case. */
    primaryRetailerData: RetailerProfileEntity | undefined;

    /** Vehicle VIN data for the case. */
    vehicleVinData: VinDetailEntity | undefined;
};

/**
 * Right sidebar for the draft case view. Displays retailer info, vehicle info,
 * case team members, and case metadata (last updated date).
 *
 * @param props - component props
 * @returns the draft case sidebar
 */
export function DraftSidebar({
    caseDetails,
    isAddMemberDisabled,
    onAddTeamMember,
    onError,
    primaryRetailerData,
    vehicleVinData,
}: DraftSidebarProps) {
    // Hooks
    const classes = useStyles();

    // Forwarded callback aliases
    const handleAddTeamMember = onAddTeamMember;
    const handleError = onError;

    // Derived state
    const retailerNumber = caseDetails?.retailerProfiles?.find((retailer) => retailer.isPrimary)?.retailerNumber || '';

    return (
        <div className={`${classes.rightContainer}`}>
            <div
                className={`${classes.rightContainerHead} ${classes.borderBottomGray} d-flex align-items-center flex-row`}
            >
                <div className="d-flex flex-column ml-4 mt-3">
                    <Typography color="blueOne" variant="h6">
                        Details
                    </Typography>
                    <div className={`${classes.borderBottomBlue} mt-3`} />
                </div>
            </div>
            <div className="ml-4 mr-4 mt-3">
                <div className="d-flex justify-content-between flex-row">
                    <Typography color="black" variant="h5">
                        Retailer
                    </Typography>
                </div>
                {primaryRetailerData && (
                    <div className="d-flex flex-column mt-3">
                        <Typography color="blueOne">
                            {primaryRetailerData.name} (#{primaryRetailerData.retailerNumber})
                        </Typography>
                        <div className="mt-1">
                            <Typography>
                                {`Region ${primaryRetailerData.regionCode}, Zone ${primaryRetailerData.zoneCode}, District ${primaryRetailerData.districtCode}`}
                            </Typography>
                        </div>
                    </div>
                )}
                <div className={`${classes.borderBottomGray} mt-5`} />

                <div className="d-flex justify-content-between mt-3 flex-row">
                    <Typography color="black" variant="h5">
                        Vehicle
                    </Typography>
                </div>
                <div className="d-flex flex-column mt-3">
                    <Typography color="blueOne">{vehicleVinData?.vin}</Typography>
                    <div className="mt-1">
                        <Typography>
                            {vehicleVinData?.modelYear} {vehicleVinData?.model}
                        </Typography>
                    </div>
                </div>

                <div className={`${classes.borderBottomGray} mt-5`} />
                <CaseTeamSection
                    isAddMemberDisabled={isAddMemberDisabled}
                    onAddTeamMember={handleAddTeamMember}
                    onError={handleError}
                    retailerNumber={retailerNumber}
                    retailerProfiles={caseDetails?.retailerProfiles || []}
                />

                <div className={`${classes.borderBottomGray} mt-5`} />

                <div className="d-flex flex-column mt-3">
                    <Typography color="black" variant="h5">
                        Case Details
                    </Typography>

                    <div className="d-flex align-items-center mt-3 flex-row">
                        <div className="d-flex flex-column">
                            <Typography variant="caption">
                                <b>Last Updated:</b>{' '}
                                {caseDetails?.modifiedOnDescription ? caseDetails.modifiedOnDescription : 'Today'}
                            </Typography>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const useStyles = createUseStyles({
    borderBottomBlue: {
        borderBottom: `2px solid ${colors.blueOne}`,
    },
    borderBottomGray: {
        borderBottom: `1px solid ${colors.grayThree}`,
    },
    rightContainer: {
        backgroundColor: colors.grayOne,
        width: '24%',
    },
    rightContainerHead: {
        height: '56px',
    },
});
